<template lang="pug">
.uc-nav-badges(
  class="nav-badge-width"
  :class="{ 'reduce-motion': suspend.reducedMotion }"
  uc-word-skip=""
  :aria-label="`An indication of whether you have a achieved the badge for this page. You earn the badge by answering enough questions correctly. Status is: ${isCompleted && isPassed ? 'passed' : isCompleted ? 'failed' : 'not completed'}.`"
  role="region"
  tabindex="-1"
  v-if="hasBadge && !isEditMode"
)
  .badge-gutter__underlay.absolute.inset-0.h-screen.w-screen.z-20.opacity-0(
    @click="closeBadges"
    :class="['bg-black/10 md_hidden', { 'hidden': !gutterAnimated }]"
  )
  .badge-gutter.opacity-0.fixed.top-0.h-screen.bg-gray-200.z-20(
    class="nav-gutter nav-badge-width"
    v-if="hasBadge"
  )
  .badge-container.flex(
    :class="[extendedNavMenu ? 'badgePositionExtended' : 'badgePosition', badgeBottom ? 'flex-col-reverse' : 'flex-col']"
  )
    .badge.opacity-0.relative.flex.items-center.justify-center.pt-2.z-20(
      v-if="computedBadgeImg"
    )
      uc-asset.badge-image.relative.max-h-80.w-full.p-1(
        :src="computedBadgeImg.src"
        :class="computedPageBadge.styles"
        @click="playConfetti"
        aria-hidden="true"
      )
    .badge-progress-container.relative.flex.items-centre.justify-center.opacity-0.py-2.overflow-hidden.z-20(
      :class="{ 'fade-top': fadeTop, 'fade-bottom': fadeBottom }"
      class="nav-badge-width"
      :style="{ 'height': progressContainerHeight }"
    )
      .progress_tracker.absolute.flex.items-centre.justify-center()
        .progress__bg.absolute.z-30(
          :style="{ 'height': progressHeight }"
        )
        .progress__bar.bg-primary-500.z-30()

      .progress-container-fade.flex.flex-col.min-w-full.overflow-hidden()
        .progress__step.items-centre.justify-center.flex.z-40(
          v-for="(question, index) in computedQuestionStatuses"
          :key="question.id"
          :data-id="question.id"
          :class="[{ 'pb-0': index === computedQuestionStatuses?.length - 1 }]"
          :aria-label="`Badge ${index + 1} of ${computedQuestionStatuses?.length}. Status is ${question.completed ? question.correct ? 'correct' : 'incorrect' : 'not attempted'}.`"
        role="img"
        )
          .progress__label.fixed.border-2.flex.justify-center.items-center.transition(
            :class="[{ 'is-current': isCurrentQuestion(question.id) }, !question.completed ? 'border-gray-400 text-gray-800' : 'is-answered', question.completed ? question.correct ? 'is-correct' : 'is-incorrect' : '']"
            :data-multiplier="labelMultiplier"
          )
            uc-text.text-sm(
              :k="`badge-question.${index}`" 
              :text="index + 1"
              v-if="!question.completed"
            )
            uc-icon(
              icon="theme:check"
              aria-hidden="true"
              v-else-if="question.correct && question.completed"
            )
            uc-icon(
              icon="theme:error"
              aria-hidden="true"
              v-else-if="!question.correct && question.completed"
            )
      uc-lightbox.p-6(
        :block="true"
        :id="name.id"
        class="w-[120ch]"
        @close="reset()"
        v-for="name in computedFeedback"
      )
        uc-screen(
          :key="s.id"
          :edit-path="`badges.targets.${s.id}.${i}`"
          :screen="s"
          v-for="(s, i) in name.templates"
          v-if="name.templates"
        )
        uc-preload-sfx(name="continue")
        template(v-slot:buttons="{ close }")
          uc-button.btn.btn-primary.px-1(
            id="reset"
            @click="close"
            text="Try again"
            data-sfx="continue"
            v-if="!feedbackButtonPassed"
          )
          uc-button.btn.btn-primary.px-1(
            id="close"
            @click="close"
            text="Continue"
            data-sfx="continue"
            v-if="feedbackButtonPassed"
          )

</template>

<script setup lang="ts">
import {
  watchEffect,
  ref,
  computed,
  onMounted,
  watch,
  inject,
  nextTick
} from 'vue';
import {
  breakpointsTailwind,
  useBreakpoints,
  useEventListener
} from '@vueuse/core';
import useQuizzes from '../stores/useQuizzes';
import useContent from '../stores/useContent';
import useSuspend from '../lib/useSuspend';
import { storeToRefs } from 'pinia';
import openLightbox from '../lib/openLightbox';
import useEditMode from '../stores/useEditMode';
import { debounce } from 'lodash-es';
import getConfetti from '../lib/getConfetti';
import colors from 'tailwindcss/colors';

const { available } = storeToRefs(useEditMode());
const isEditMode = computed(() => available.value);

const { compiled: content } = storeToRefs(useContent());
const suspend = useSuspend();

const { computeQuizQuestionStatuses } = storeToRefs(useQuizzes());
const { statuses } = useQuizzes();
const badges = content?.value?.badges;
const hasSpeech = inject('hasSpeech');
const hasAudio = inject('hasAudio');
const hasVideo = inject('hasVideo');
const hasSfx = inject('hasSfx');
const units = inject('units');

const urlRegex = /^https?:\/\//;
const fromYMargin = ref(0);
const toYMargin = ref(0);
const offsetMultiplier = ref(1);
const fadeTop = ref();
const fadeBottom = ref();
const gutterAnimated = ref(false);
const badgesOpen = ref(false);
const currentSection = ref<HTMLElement | null>(null);
const breakpoints = useBreakpoints(breakpointsTailwind);
const mediumDevice = breakpoints.greaterOrEqual('md');

let tl;

const props = defineProps<{
  pageId: string;
}>();

const badgeBottom = computed(() => {
  return computedPageBadge.value['badge-position'] === 'bottom';
});

const hasMenuButton = computed(() =>
  document.querySelector('.menu__button') ? true : false
);

const extendedNavMenu = computed(() => {
  return (
    (hasSpeech.value || hasAudio.value || hasVideo.value || hasSfx.value) &&
    hasMenuButton.value
  );
});

const isCompleted = computed(() => {
  return statuses[computedPageBadge.value['for-quiz']]?.completed;
});

const isPassed = computed(() => {
  return statuses[computedPageBadge.value['for-quiz']]?.passed;
});

const computedQuestionStatuses = computed(() => {
  const quizzesFromStore = computeQuizQuestionStatuses.value;
  const quizId = computedPageBadge.value['for-quiz'];
  if (quizzesFromStore.has(quizId)) {
    return quizzesFromStore.get(quizId);
  } else {
    return [];
  }
});

const computedBadgeImg = computed(() => {
  if (
    !computedPageBadge.value['img-default'] &&
    !computedPageBadge.value['img-success']
  )
    return;
  let badgeObject = {
    src: computedPageBadge.value['img-default']
  };
  if (
    statuses[computedPageBadge.value['for-quiz']]?.passed &&
    statuses[computedPageBadge.value['for-quiz']]?.completed &&
    computedPageBadge.value['img-success']
  ) {
    badgeObject.src = computedPageBadge.value['img-success'];
  } else if (
    statuses[computedPageBadge.value['for-quiz']]?.failed &&
    statuses[computedPageBadge.value['for-quiz']]?.completed &&
    computedPageBadge.value['img-failed']
  ) {
    badgeObject.src = computedPageBadge.value['img-failed'];
  }

  return badgeObject;
});

const labelMultiplier = 58;

const hasBadge = computed(() => {
  if (!badges) return false;
  if (computedQuestionStatuses?.value.length === 0) return false;
  if (computedPageBadge.value) {
    return true;
  } else {
    return false;
  }
});

// get the badge for this page - one badge per page
// TODO - get this from useBadges
const computedPageBadge = computed(() => {
  if (!badges) return false;
  const defaultBadge = Object.assign({}, badges);
  delete defaultBadge.pages;

  const pageBadge = Object.assign(
    defaultBadge,
    badges.pages?.find(element => element.page === props.pageId)
  );
  // If no for-quiz force value to match page Id
  if (!pageBadge['for-quiz']) pageBadge['for-quiz'] = props.pageId;
  if (pageBadge) {
    return pageBadge;
  } else {
    return false;
  }
});

const progressHeight = computed(() => {
  const dottedMultiplier = computedQuestionStatuses.value.length - 1;
  const progressHeightPx = dottedMultiplier * labelMultiplier;
  return progressHeightPx + 'px';
});

const progressContainerHeight = computed(() => {
  const dottedMultiplier =
    computedQuestionStatuses.value.length > 7
      ? 7
      : computedQuestionStatuses.value.length;
  const progressHeightPx = dottedMultiplier * labelMultiplier;
  return progressHeightPx + 'px';
});

// generate lightboxes
const computedFeedback = computed(() => {
  if (
    !computedPageBadge.value['lightbox-passed'] &&
    !computedPageBadge.value['lightbox-failed'] &&
    !computedPageBadge.value['lightbox-unachievable']
  )
    return;
  const feedbackLightboxes = [];
  if (computedPageBadge.value['lightbox-passed'])
    feedbackLightboxes.push({
      id: 'lightbox-passed',
      template: 'lightbox',
      templates: computedPageBadge.value['lightbox-passed']
    });
  if (computedPageBadge.value['lightbox-failed'])
    feedbackLightboxes.push({
      id: 'lightbox-failed',
      template: 'lightbox',
      templates: computedPageBadge.value['lightbox-failed']
    });
  if (computedPageBadge.value['lightbox-unachievable'])
    feedbackLightboxes.push({
      id: 'lightbox-unachievable',
      template: 'lightbox',
      templates: computedPageBadge.value['lightbox-unachievable']
    });
  return feedbackLightboxes;
});

const feedbackButtonPassed = computed(() => {
  if (statuses[computedPageBadge.value['for-quiz']].completed) return true;
  return false;
});

const currentSectionId = computed(() => {
  return currentSection.value?.getAttribute('id');
});

const isCurrentQuestion = questionId => {
  if (questionId === currentSectionId.value) {
    return true;
  } else if (currentSection.value) {
    const allNestedScreenIds = [
      ...currentSection.value.querySelectorAll('.screen')
    ].map(s => s.getAttribute('id'));

    // is the question a nested screen in the current section (templates, splits, etc.)?
    return allNestedScreenIds.findIndex(id => id === questionId) > -1;
  }

  return false;
};

const setupTimeline = () => {
  const fromOpacity = 0;
  const toOpacity = 1;
  const fromX = -100;
  const toX = 0;

  tl = gsap.timeline({
    paused: true
  });

  tl.to('.bottom-left-logo', { autoAlpha: 0, overwrite: true })
    .fromTo(
      '.badge-gutter',
      { autoAlpha: fromOpacity },
      { autoAlpha: toOpacity, duration: 1, ease: 'linear', stagger: 0.25 }
    )
    .fromTo(
      '.badge-progress-container',
      { x: fromX, autoAlpha: fromOpacity },
      {
        x: toX,
        duration: 3,
        ease: 'elastic.out(1.25,1)',
        stagger: 0.25,
        autoAlpha: toOpacity,
        onComplete: () => {
          animateProgressBar(computedQuestionStatuses.value, []);
        }
      },
      '<'
    )
    .fromTo(
      '.badge',
      { x: fromX, autoAlpha: fromOpacity },
      {
        x: toX,
        duration: 3,
        ease: 'elastic.out(1.25,1)',
        stagger: 0.25,
        autoAlpha: toOpacity
      },
      '<'
    );
};

const openBadges = () => {
  animateGutter(true, true);
};

const closeBadges = () => {
  if (mediumDevice.value) return;
  animateGutter(false, true);
};

const animateGutter = (newValue, force) => {
  if (!force && !mediumDevice.value) return;

  gutterAnimated.value = newValue;

  tl.delay(newValue ? 1 : 0);
  tl.timeScale(newValue ? 1 : 4);

  if (suspend.reducedMotion) {
    tl.progress(newValue ? 1 : 0);
  }

  newValue ? tl.play() : tl.reverse();
};

// show/hide the badges side bar depending on screen type
const showOrHideBadges = debounce(
  () => {
    if (currentSection.value === null) return;

    const hideBadges = currentSection.value?.querySelector(
      '.screen--course-title, .screen--unit-title, .screen--unit-end, .screen--dashboard, .screen--video'
    );

    if (hideBadges || !mediumDevice.value) {
      animateGutter(false, true);
    } else {
      if (!gutterAnimated.value) {
        animateGutter(true, false);
      }
    }
  },
  300,
  { trailing: true }
);

onMounted(async () => {
  if (hasBadge.value) {
    setupTimeline();

    useEventListener(window, 'scrollsectionentered', e => {
      currentSection.value = e.target as HTMLElement;
      showOrHideBadges();
    });

    useEventListener(document, 'toggleBadges', e => {
      gutterAnimated.value ? closeBadges() : openBadges();
    });
  }
});

watch(computedQuestionStatuses, animateProgressBar);
watch(statuses, checkPassed);
watch(computedBadgeImg, animateBadge);
watch(mediumDevice, () => {
  showOrHideBadges();
  // if (mediumDevice.value && !gutterAnimated.value) openBadges();
  // else if (!mediumDevice.value && gutterAnimated.value) closeBadges();
});

watchEffect(() => {
  fadeTop.value = toYMargin.value < 0;
  fadeBottom.value =
    offsetMultiplier.value + 5 !== computedQuestionStatuses.value?.length;
});

function animateBadge(newValue, oldValue) {
  if (!hasBadge.value) return;
  if (!oldValue || newValue.src === oldValue.src) return;
  const tBadge = gsap.timeline({
    paused: true,
    onComplete: () => {
      playConfetti();
    }
  });

  tBadge
    .to('.badge-image', { scale: 1.5, repeat: 1, yoyo: true, yoyoEase: true })
    .to('.badge-image', { scale: 1 });

  tBadge.play();
}

const randomRange = (min, max) => Math.random() * (max - min) + min;
const primaryColors = colors[content.value?.course?.primary] ?? colors.blue;
const secondaryColors =
  colors[content.value?.course?.secondary] ?? colors.yellow;
const colours = [
  primaryColors[300],
  primaryColors[500],
  primaryColors[800],
  secondaryColors[300],
  secondaryColors[500],
  secondaryColors[800]
];

function playConfetti() {
  if (isCompleted.value && isPassed.value && !suspend.reducedMotion) {
    const badgeImageEl = document.querySelector(
      mediumDevice.value
        ? '.uc-nav-badges .badge-image'
        : '.uc-nav-menu .badge-image'
    ) as HTMLElement;

    getConfetti(badgeImageEl, colours);
  }
}

function animateProgressBar(newValue, oldValue) {
  if (!hasBadge.value) return;
  if (!oldValue || !newValue || !gutterAnimated.value) return;

  let prevCompleted = 0;
  let newCompleted = 0;

  if (oldValue) {
    for (const item of oldValue) {
      if (item.completed) prevCompleted++;
    }
  }

  if (newValue) {
    for (const item of newValue) {
      if (item.completed) newCompleted++;
    }
  }
  // if returning to completed quiz animate upto final question
  if (
    prevCompleted === 0 &&
    newCompleted === computedQuestionStatuses?.value.length
  ) {
    newCompleted--;
  }
  // final question does not require progress animation
  if (
    prevCompleted === newCompleted ||
    newCompleted === computedQuestionStatuses?.value.length
  )
    return;

  if (prevCompleted !== 0 && newCompleted === 0) {
    fromYMargin.value = 0;
    toYMargin.value = 0;
  }

  // if reseting a completed quiz animate progress bar from final question, we want to count the fences not the posts.
  if (
    newCompleted === 0 &&
    prevCompleted === computedQuestionStatuses?.value.length
  ) {
    prevCompleted--;
  }

  const fromHeight = labelMultiplier * prevCompleted;
  const toHeight = labelMultiplier * newCompleted;

  const t2 = gsap.timeline({ paused: true });

  // move the progress bar if more than 6 from the begining or 3 from the end
  if (
    newValue.length > 6 &&
    newCompleted >= 5 &&
    newCompleted < newValue.length - 2
  ) {
    fromYMargin.value = toYMargin.value;
    toYMargin.value = offsetMultiplier.value * -Math.abs(labelMultiplier);
    offsetMultiplier.value++;

    t2.fromTo(
      '.progress__bar, .progress__bg, .progress-container-fade',
      { marginTop: fromYMargin.value },
      {
        marginTop: toYMargin.value,
        duration: 1,
        ease: 'power2.out',
        delay: 0.25
      }
    );
  }

  // reset the progress bar only when there are more than 6 questions
  if (newValue.length > 6 && newCompleted === 0 && prevCompleted > 0) {
    fromYMargin.value = offsetMultiplier.value * -Math.abs(labelMultiplier);
    offsetMultiplier.value = 1;

    t2.fromTo(
      '.progress__bar, .progress__bg, .progress-container-fade',
      { marginTop: fromYMargin.value },
      {
        marginTop: toYMargin.value,
        duration: 1,
        ease: 'power2.out',
        delay: 0.25
      }
    );
  }

  t2.fromTo(
    '.progress__bar',
    { height: fromHeight },
    { height: toHeight, duration: 1, ease: 'power2.out', stagger: 1.25 }
  );

  t2.play();
}

async function checkPassed(newValue, oldValue) {
  const quizId = computedPageBadge.value['for-quiz'];
  if (!newValue[quizId]?.completed) {
    if (suspend.badges && suspend.badges[quizId]) {
      suspend.badges[quizId].notified = false;
    }
  }

  // checkPassed fires more than once to wait until the final quiz status is set
  await nextTick();
  await nextTick();

  if (newValue[quizId]?.passed && computedPageBadge.value['lightbox-passed']) {
    maybeOpenLightbox('lightbox-passed', quizId);
  } else if (
    newValue[quizId]?.failed &&
    computedPageBadge.value['lightbox-failed']
  ) {
    maybeOpenLightbox('lightbox-failed', quizId);
  }

  if (
    !oldValue[quizId]?.completed &&
    statuses[quizId]?.achievable === false &&
    computedPageBadge.value['lightbox-unachievable']
  ) {
    maybeOpenLightbox('lightbox-unachievable', quizId);
  }
}

const maybeOpenLightbox = (boxName, quizId) => {
  // TODO - tidy up the syntax
  if (suspend.badges && suspend.badges[quizId]?.notified) return;
  if (!suspend.badges) suspend.badges = [];
  if (!suspend.badges[quizId]) suspend.badges[quizId] = {};

  if (boxName && !urlRegex.test(boxName)) {
    suspend.badges[quizId].notified = true;
    openLightbox(boxName);
  }
};

// FIXME - this should be part of useQuizzes store and not a duplicate of results reset
const reset = () => {
  // don't reset if passed or failed
  if (statuses[computedPageBadge.value['for-quiz']].completed) return;

  const page = document.querySelector('.page') as HTMLElement;
  const scroller = document.querySelector('.scroller') as HTMLElement;
  stop();
  gsap.to(scroller, {
    duration: 0.5,
    scrollTo: 0,
    onComplete: () => {
      Array.from(scroller.querySelectorAll('[id]')).forEach(el => {
        const id = el.getAttribute('data-id') || el.id;
        // TODO - use SCORM set to update suspend.completed
        delete suspend.completed[id];
        if (
          suspend.badges &&
          suspend.badges[computedPageBadge.value['for-quiz']]
        ) {
          suspend.badges[computedPageBadge.value['for-quiz']].notified = false;
        }

        if (suspend.questionOptions[id]) {
          suspend.questionOptions = [];
        }
      });

      page.refreshSections();
    }
  });
};
</script>

<style scoped>
@keyframes spin {
  0% {
    transform: rotateZ(0);
  }

  100% {
    transform: rotateZ(720deg);
  }
}

.nav-gutter {
  padding-left: 26px;
}

.badgePositionExtended {
  margin-top: 210px;
}

.badgePosition {
  margin-top: 140px;
}

.progress__bar {
  top: 0px;
  width: 2px;
}

.progress__bg {
  top: 0px;
  width: 2px;
  border-left: 2px dotted lightgrey;
  height: 100%;
}

.progress__label {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: #fff;
}

.is-answered {
  @apply bg-primary-500;
  @apply border-primary-500;
  color: #fff;
}

.is-answered .icon {
  animation: spin 1s ease-out both;
}

.reduce-motion .is-answered .icon {
  animation: none;
}

.is-current {
  @apply scale-150;
}

.is-current:not(.is-answered) {
  @apply text-primary-800 border-primary-500;
}

.progress__label .icon {
  display: table-cell;
  vertical-align: middle;
  text-align: center;
}

.progress__step {
  position: relative;
  padding-bottom: 58px;
}

/* TODO - make this conditional on badge-gutter: true 
@media (min-width: 1280px) {
  .nav-buttons-container {
    padding-left: 2.4em;
  }
}
*/

.badge-progress-container {
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    black var(--top-mask-size, 0),
    black calc(100% - var(--bottom-mask-size, 0)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    black var(--top-mask-size, 0),
    black calc(100% - var(--bottom-mask-size, 0)),
    transparent 100%
  );
  --top-mask-size: 0px;
  --bottom-mask-size: 0px;
}

.fade-bottom {
  --bottom-mask-size: 48px !important;
}

.fade-top {
  --top-mask-size: 48px !important;
}

.nav-badge-width {
  width: 4em;
}

@media (min-width: 1440px) {
  .nav-badge-width {
    width: 7em;
  }

  .badge-image {
    padding: 0.5rem; /* matches Tailwind p-2 */
  }
}
</style>
